/**
 * One data-fetching approach for the whole app: TanStack Query over the `api` client.
 *
 * The retry policy is the interesting part. Retrying a 403 is pointless (the permission
 * will not appear in 200ms), retrying a 422 is pointless (the payload is still wrong),
 * and retrying a 401 fights the refresh interceptor. Only genuine transport and 5xx
 * failures are worth a second attempt.
 */
import { QueryClient } from '@tanstack/react-query';

import { ApiError, isApiError } from './client';

function shouldRetry(failureCount: number, error: unknown): boolean {
  if (failureCount >= 2) return false;
  if (!isApiError(error)) return false;
  return error.kind === 'NETWORK' || error.kind === 'SERVER';
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: shouldRetry,
      // Enterprise data is not real-time. Refetching on every window focus turns a
      // second monitor into a request generator.
      refetchOnWindowFocus: false,
      staleTime: 30_000,
      gcTime: 5 * 60_000,
    },
    mutations: {
      retry: false,
    },
  },
});

/** Narrow a react-query `error` to `ApiError` for rendering. */
export function asApiError(error: unknown): ApiError | null {
  return isApiError(error) ? error : null;
}
